// The top-bar bell (NOTIFY-01): unread count badge, and a compact dropdown like the wallet summary
// (owner, 2026-10-25): What's new, messages from Adyvance, the latest notifications one line each,
// mark all read, and a link to the centre. Live via the realtime hub; polling every 30 s when that is not
// available (the server says so truthfully: activation_required).
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { cx, Icon, SkeletonText } from "@ady/ui";
import { useShell } from "../shell/context";
import {
  AdyvanceNotices,
  useUnseenNotices,
  useWhatsNew,
  WhatsNewBell,
} from "../shell/ControlCentre";
import { usePopover } from "../shell/usePopover";
import { TID } from "../testids";
import {
  notificationsApi,
  notifyKeys,
  useNotificationList,
  useUnreadCount,
  type AppNotification,
} from "./api";
import { POLL_MS, badgeText, useLiveNotifications } from "./live";
import { AllNotificationsPopup } from "./AllNotifications";
import { NotificationItem } from "./NotificationList";
import { NTF_TID } from "./testids";

export function NotificationBell({ className }: { className: string }) {
  const { membership } = useShell();
  const orgId = membership.org.id;
  const { open, setOpen, ref } = usePopover();
  const live = useLiveNotifications(orgId);
  const poll = live === "polling" ? POLL_MS : 0;
  const unread = useUnreadCount(orgId, poll);
  const list = useNotificationList(orgId, false, open ? poll : 0);
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: notifyKeys.all(orgId) });
  const markRead = useMutation({
    mutationFn: (n: AppNotification) => notificationsApi.setRead(orgId, n.id, true),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => notificationsApi.readAll(orgId),
    onSuccess: refresh,
  });
  // D-065: unseen messages from Adyvance count too.
  const count = (unread.data?.unread ?? 0) + useUnseenNotices() + (useWhatsNew().data?.unseen ?? 0);
  const badge = badgeText(count);
  const items = (list.data?.data ?? []).slice(0, 8);
  const close = () => setOpen(false);
  const [all, setAll] = useState(false);
  return (
    <>
      <div className="relative flex-none" ref={ref}>
        <button
          type="button"
          className={cx("relative", className, open && "border-link bg-selected")}
          aria-label={
            count > 0
              ? t("notifications.bell.labelUnread", { count })
              : t("notifications.bell.label")
          }
          aria-expanded={open}
          aria-haspopup="dialog"
          data-testid={TID.notificationsButton}
          onClick={() => setOpen((v) => !v)}
        >
          <Icon name="bell" gradient />
          {badge ? (
            <span
              data-testid={NTF_TID.bellBadge}
              className="absolute -end-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-danger-bg px-1 text-2xs font-semibold text-danger-fg"
            >
              {badge}
            </span>
          ) : null}
        </button>
        {open ? (
          <div
            role="dialog"
            aria-label={t("notifications.centre.title")}
            data-testid={TID.notificationsPanel}
            className="absolute end-0 top-full z-(--ady-z-popover) mt-2 flex w-72 max-w-[calc(100vw-1rem)] flex-col rounded-md border border-subtle bg-overlay shadow-2"
          >
            <div className="flex items-center justify-between gap-2 border-b border-subtle px-3 py-2">
              <span className="flex items-center gap-1.5 text-xs font-semibold">
                {t("notifications.centre.title")}
                {count > 0 ? (
                  <span className="rounded-full bg-danger-bg px-1.5 text-2xs font-semibold text-danger-fg">
                    {badge}
                  </span>
                ) : null}
              </span>
              {(unread.data?.unread ?? 0) > 0 ? (
                <button
                  type="button"
                  className="text-2xs font-semibold text-link disabled:opacity-60"
                  disabled={readAll.isPending}
                  onClick={() => readAll.mutate()}
                  data-testid={NTF_TID.markAllRead}
                >
                  {t("notifications.centre.markAllRead")}
                </button>
              ) : null}
            </div>
            <div className="flex max-h-96 flex-col gap-1.5 overflow-y-auto p-1.5">
              <WhatsNewBell onOpen={close} />
              <AdyvanceNotices open={open} compact />
              {list.isPending ? (
                <div className="px-2 py-1">
                  <SkeletonText lines={3} />
                </div>
              ) : items.length === 0 ? (
                <span className="flex items-center gap-2 px-2 py-3 text-xs text-fg-tertiary">
                  <Icon name="checkCircle" />
                  {t("notifications.centre.empty")}
                </span>
              ) : (
                <ul className="flex flex-col" data-testid={NTF_TID.bellList}>
                  {items.map((n) => (
                    <NotificationItem
                      key={n.id}
                      n={n}
                      compact
                      onOpen={(x) => {
                        if (!x.read) markRead.mutate(x);
                        close();
                      }}
                    />
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between gap-2 border-t border-subtle px-3 py-2">
              <button
                type="button"
                onClick={() => {
                  close();
                  setAll(true);
                }}
                className="whitespace-nowrap text-xs font-semibold text-link hover:underline"
                data-testid={NTF_TID.bellViewAll}
              >
                {t("notifications.centre.viewAll")}
              </button>
              <span
                className="inline-flex items-center gap-1 whitespace-nowrap text-2xs text-fg-tertiary"
                title={live === "live" ? undefined : t("notifications.centre.polling")}
                data-testid={NTF_TID.liveState}
              >
                <span
                  className={cx(
                    "size-1.5 rounded-full",
                    live === "live" ? "bg-success-fg" : "bg-fg-tertiary",
                  )}
                  aria-hidden
                />
                {live === "live"
                  ? t("notifications.centre.live")
                  : t("notifications.centre.pollingShort")}
              </span>
            </div>
          </div>
        ) : null}
      </div>
      <AllNotificationsPopup open={all} onClose={() => setAll(false)} />
    </>
  );
}
